import { useState } from "react";

import { Button, Classes, NumericInput, Spinner } from "@blueprintjs/core";
import styled from "@emotion/styled";

import CreditSVG from "@/../../hypetrain/static/img/credit.svg";
import { EmbedSvg } from "@/components/helpers/ui/EmbedSvg";
import { Box, Flex } from "@/components/layout/flexbox";
import { breakpoints } from "@/components/layout/Media";
import { PrettyBox } from "@/components/pieces/PrettyBox";
import { Avatar } from "@/components/pieces/users/Avatar";
import { BP_NS, SITE_NAME } from "@/css/constants";
import { PendingOrderQuery, useSetExtraCreditsMutation } from "@/graphql";
import { PendingOrder } from "@/types/products";
import { NonNullableArrayItem } from "@/types/utils";
import { parseDecimal } from "@/utils/string";

type Product = NonNullableArrayItem<
  NonNullable<
    NonNullable<PendingOrderQuery["pendingOrder"]>["coach"]
  >["activeCoachProducts"]
>;

interface ProductRowProps {
  coachProduct: Product;
  loading: boolean;
  disabled: boolean;
  quantity: string;
  setQuantity: (obj: Record<string, string>) => void;
}

function ProductRow({
  coachProduct,
  loading,
  disabled,
  quantity,
  setQuantity,
}: ProductRowProps) {
  return (
    <ReponsiveFlex
      my={[0, 24]}
      py={[3, 0]}
      px={[3, 24]}
      alignItems="center"
      justifyContent="space-between"
      flexWrap="wrap"
      className="bbs"
    >
      <Flex alignItems="center" width={["100%", "auto"]} flexWrap="wrap">
        <Box fontSize={18} fontWeight={500}>
          {coachProduct.product.name}
        </Box>
        <Flex ml={[0, 3]} alignItems="center">
          <EmbedSvg src={CreditSVG} height={18} width={18} />
          <Box ml={1}>{coachProduct.coachPrice} credits / session</Box>
        </Flex>
        {loading && (
          <Box ml={3}>
            <Spinner size={16} />
          </Box>
        )}
      </Flex>
      <IntSelector mt={[3, 0]} alignItems="center" width={["100%", 160]}>
        <Button
          text="-"
          disabled={parseDecimal(quantity) == 0 || disabled}
          onClick={() => {
            const newQuantity = parseDecimal(quantity) - 1;
            if (newQuantity < 0) {
              setQuantity({ [coachProduct.id]: "0" });
            } else {
              setQuantity({ [coachProduct.id]: newQuantity.toString() });
            }
          }}
        />
        <NumericInput
          style={{ textAlign: "center" }}
          large={true}
          fill={true}
          value={quantity}
          minorStepSize={1}
          disabled={disabled}
          buttonPosition="none"
          onValueChange={(valueAsNumber, valueAsString) => {
            if (loading || valueAsString === quantity || isNaN(valueAsNumber)) {
              return;
            } else if (valueAsNumber < 0) {
              setQuantity({ [coachProduct.id]: "0" });
            } else {
              setQuantity({ [coachProduct.id]: valueAsString });
            }
          }}
        />
        <Button
          text="+"
          disabled={disabled}
          onClick={() => {
            setQuantity({
              [coachProduct.id]: (parseDecimal(quantity) + 1).toString(),
            });
          }}
        />
      </IntSelector>
    </ReponsiveFlex>
  );
}

interface PackageBuilderProps {
  processing: boolean;
  order: PendingOrder;
  setOrder: (order: PendingOrder) => void;
}

export function PackageBuilder({
  processing,
  order,
  setOrder,
}: PackageBuilderProps) {
  const [callQueue, setCallQueue] = useState<(() => void)[]>([]);
  const [quantities, setQuantities] = useState<Record<string, string>>({});

  const [setExtraCredits, { loading: submitting }] = useSetExtraCreditsMutation(
    {
      onCompleted: (response) => {
        // Process the call queue if we need to
        if (callQueue.length > 0) {
          // Only call the last item in the call queue
          // We don't need to process all of the intermediate points
          callQueue.at(-1)?.();
          // And then wipe the queue
          setCallQueue([]);
        }

        if (response.setExtraCredits?.error) return;

        setOrder(response.setExtraCredits?.order!);
      },
    },
  );

  const coachProducts = order.coach?.activeCoachProducts ?? [];

  function onSetQuantity(obj: Record<string, string>) {
    const newQuantities = {
      ...quantities,
      ...obj,
    };

    setQuantities(newQuantities);

    const total = Object.keys(newQuantities).reduce((t, id) => {
      const price = coachProducts.find((p) => p.id == id)?.coachPrice;
      return t + parseDecimal(newQuantities[id]) * parseDecimal(price);
    }, 0);
    if (submitting) {
      // If we are in the middle of an API call, queue the calls
      setCallQueue([
        ...callQueue,
        () => {
          setExtraCredits({
            variables: {
              orderId: order.id,
              numCredits: total.toString(),
            },
          });
        },
      ]);
    } else {
      setExtraCredits({
        variables: {
          orderId: order.id,
          numCredits: total.toString(),
        },
      });
    }
  }

  const relevantSpecialtyIds = Object.keys(
    order.orderItems?.reduce(
      (agg, oi) => {
        agg[oi?.product?.specialtyId || ""] = 1;
        return agg;
      },
      {} as Record<string, number>,
    ) || {},
  );

  const relevantCoachProducts = coachProducts.filter(
    (cp) =>
      !cp.product?.introProduct &&
      relevantSpecialtyIds.includes(cp.product?.specialtyId || ""),
  );

  return (
    <PrettyBox>
      <Box fontSize={22} fontWeight={600}>
        Commit and Save
      </Box>
      <Box mt={2}>
        Services on {SITE_NAME} are priced in credits. The more credits you buy,
        the bigger discounts you unlock. Credits can be used with any coach on{" "}
        {SITE_NAME}, so you can try multiple coaches.
      </Box>
      <Box className="bas round-corners" backgroundColor="#FFF" mt={4}>
        <Flex
          alignItems="center"
          className="bbs"
          py={3}
          px={3}
          fontWeight={600}
        >
          <Avatar size={24} user={order.coach} />
          <Box ml={1}>{order.coach?.name}'s Services</Box>
        </Flex>
        {relevantCoachProducts.map((cp) => (
          <ProductRow
            key={cp.id}
            coachProduct={cp}
            quantity={quantities[cp.id] || "0.00"}
            loading={submitting}
            disabled={processing}
            setQuantity={onSetQuantity}
          />
        ))}
      </Box>
    </PrettyBox>
  );
}

const ReponsiveFlex = styled(Flex)`
  border-bottom-width: 0px !important;

  @media screen and (max-width: ${breakpoints.sm - 1}px) {
    border-bottom-width: 1px !important;
  }
`;

const IntSelector = styled(Flex)`
  .${Classes.BUTTON}:not([class*='${BP_NS}-intent-']) {
    background-color: #f6f6fb;
    height: 40px;

    &:first-of-type {
      border-top-right-radius: 0px;
      border-bottom-right-radius: 0px;
    }
    &:last-of-type {
      border-top-left-radius: 0px;
      border-bottom-left-radius: 0px;
    }
  }
  .${Classes.INPUT_GROUP} .${Classes.INPUT} {
    border-radius: 0px;
    &:disabled {
      border-radius: 0px;
    }
  }
`;
